import { useCallback, useEffect, useState } from "react";
import { CalendarClock, Clock, HandHeart, Loader2, Plus, ShieldAlert, ShieldCheck, UserPlus } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, PrimaryButtonClass, Table, Td, Th } from "@/components/common/Chrome";
import { refOf, rowClick, useRowDetail } from "@/components/common/DetailSheet";
import { LogTimeDialog } from "@/components/common/LogTimeDialog";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { shortDate } from "@/lib/format";

const BLANK = {
  name: "", email: "", phone: "", role: "field_volunteer", location: "",
  skills: "", availability: "weekends", clearance_status: "none",
  clearance_reference: "", clearance_expires_on: "", notes: "",
};

const CLEARANCE_STYLE = {
  cleared: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  pending: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  expired: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
  none: "border-border bg-muted text-muted-foreground",
  refused: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
};

const ROLES = [
  ["field_volunteer", "Field volunteer"],
  ["volunteer_coordinator", "Volunteer coordinator"],
];

export default function Volunteers() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const detail = useRowDetail("volunteer", () => load());
  const [works, setWorks] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [clearanceFor, setClearanceFor] = useState(null);
  const [clearance, setClearance] = useState({ clearance_status: "cleared", clearance_reference: "", clearance_expires_on: "" });
  const [assignFor, setAssignFor] = useState(null);
  const [workItemId, setWorkItemId] = useState("");
  const [blockNote, setBlockNote] = useState("");
  const [timeFor, setTimeFor] = useState(null);

  const load = useCallback(async () => {
    const [v, w] = await Promise.all([api.get("/volunteers"), api.get("/works")]);
    setData(v.data);
    setWorks(w.data.items || []);
  }, []);

  useEffect(() => { load().catch(() => setData({ items: [], counters: {} })); }, [load]);

  const save = async () => {
    setBusy("save");
    try {
      await api.post("/volunteers", {
        ...form,
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
        availability: [form.availability],
        clearance_expires_on: form.clearance_expires_on || null,
      });
      toast.success(`${form.name} added to the roster`);
      setOpen(false);
      setForm(BLANK);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy("");
  };

  const saveClearance = async () => {
    setBusy("clearance");
    try {
      await api.post(`/volunteers/${clearanceFor.id}/clearance`, {
        ...clearance,
        clearance_expires_on: clearance.clearance_expires_on || null,
      });
      toast.success(`Safeguarding clearance recorded for ${clearanceFor.name}`);
      setClearanceFor(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy("");
  };

  const assign = async () => {
    setBusy("assign");
    setBlockNote("");
    try {
      const { data: res } = await api.post(`/volunteers/assign/${workItemId}`, {
        volunteer_id: assignFor.id, role_on_item: "volunteer",
      });
      toast.success(`${assignFor.name} assigned to ${res.assignment.title}`);
      setAssignFor(null);
      setWorkItemId("");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const message = typeof detail === "object" ? detail.message : apiError(e);
      setBlockNote(message);
      toast.error("Assignment blocked by the safeguarding gate");
    }
    setBusy("");
  };

  if (!data) return <Loading label="Loading volunteer roster" />;
  const { items, counters } = data;
  const sensitive = works.filter((w) => w.requires_safeguarding_clearance);
  const selectedWork = works.find((w) => w.id === workItemId);
  const gateWouldBlock = Boolean(
    selectedWork?.requires_safeguarding_clearance && assignFor && assignFor.clearance_state !== "cleared",
  );

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SCR-020 · Volunteer Roster"
        title="Nobody works with children or vulnerable adults without a live clearance"
        description="Track every volunteer's safeguarding check and its expiry. Assignment to a safeguarding-sensitive work item is refused server-side when the clearance is missing, pending or expired — and the refusal is audited."
        actions={can("volunteer", "write") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-volunteer-button" className={PrimaryButtonClass}>
                <Plus className="mr-1.5 h-4 w-4" /> Add volunteer
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Add a volunteer</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <F label="Full name" full>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                         data-testid="volunteer-name-input" />
                </F>
                <F label="Email">
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                         data-testid="volunteer-email-input" className="font-mono text-xs" />
                </F>
                <F label="Phone">
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                         data-testid="volunteer-phone-input" />
                </F>
                <F label="Role">
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="volunteer-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Location">
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                         data-testid="volunteer-location-input" />
                </F>
                <F label="Skills (comma separated)" full>
                  <Input value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })}
                         data-testid="volunteer-skills-input" placeholder="teaching, first_aid" />
                </F>
                <F label="Clearance status">
                  <Select value={form.clearance_status} onValueChange={(v) => setForm({ ...form, clearance_status: v })}>
                    <SelectTrigger data-testid="volunteer-clearance-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not held</SelectItem>
                      <SelectItem value="pending">Check submitted</SelectItem>
                      <SelectItem value="cleared">Cleared</SelectItem>
                      <SelectItem value="refused">Refused</SelectItem>
                    </SelectContent>
                  </Select>
                </F>
                <F label="Clearance expiry">
                  <Input type="date" value={form.clearance_expires_on}
                         onChange={(e) => setForm({ ...form, clearance_expires_on: e.target.value })}
                         data-testid="volunteer-expiry-input" className="font-mono" />
                </F>
              </div>
              <Button onClick={save} disabled={busy === "save" || !form.name.trim()}
                      data-testid="volunteer-save-button" className={PrimaryButtonClass}>
                {busy === "save" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Add to roster
              </Button>
            </DialogContent>
          </Dialog>
        )}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <Kpi testId="kpi-volunteers-total" label="Volunteers" value={counters.total ?? 0} icon={HandHeart} />
        <Kpi testId="kpi-volunteers-cleared" label="Cleared" value={counters.cleared ?? 0} accent="emerald" icon={ShieldCheck} />
        <Kpi testId="kpi-volunteers-pending" label="Pending / none" value={counters.pending ?? 0} accent="amber" icon={CalendarClock} />
        <Kpi testId="kpi-volunteers-expired" label="Expired clearance" value={counters.expired ?? 0} accent="red" icon={ShieldAlert} />
      </div>

      <Panel testId="panel-volunteers" kicker="FR-VOL-001/002" title="Roster">
        <Table testId="volunteers-table"
               head={<><Th>Volunteer</Th><Th>Role</Th><Th>Location</Th><Th>Skills</Th><Th>Clearance</Th><Th>Checked</Th><Th>Expires</Th><Th>Assignments</Th><Th /></>}>
          {items.length === 0 && <Empty colSpan={9} label="No volunteers on the roster" />}
          {items.map((v) => (
            <tr key={refOf(v)} className="row-hover cursor-pointer" onClick={rowClick(() => detail.show(v, refOf(v)))} data-testid={`volunteer-row-${v.id}`}>
              <Td>
                <div className="font-medium text-foreground">{v.name}</div>
                <div className="font-mono text-[11px] text-muted-foreground">{v.email || "—"}</div>
              </Td>
              <Td className="text-[11px] text-muted-foreground">{v.role.replace(/_/g, " ")}</Td>
              <Td className="text-[11px]">{v.location || "—"}</Td>
              <Td>
                <div className="flex flex-wrap gap-1">
                  {(v.skills || []).slice(0, 3).map((s) => (
                    <Chip key={s} className="border-border bg-muted text-muted-foreground">{s}</Chip>
                  ))}
                </div>
              </Td>
              <Td>
                <Chip testId={`clearance-${v.id}`} className={CLEARANCE_STYLE[v.clearance_state] || CLEARANCE_STYLE.none}>
                  {v.clearance_state}
                </Chip>
                {v.clearance_reference && (
                  <div className="mt-1 font-mono text-[10px] text-muted-foreground/70">{v.clearance_reference}</div>
                )}
              </Td>
              <Td className="whitespace-nowrap font-mono text-[11px]">{v.clearance_checked_on ? shortDate(v.clearance_checked_on) : "—"}</Td>
              <Td className="whitespace-nowrap font-mono text-[11px]">{v.clearance_expires_on ? shortDate(v.clearance_expires_on) : "—"}</Td>
              <Td>
                {(v.assignments || []).length === 0
                  ? <span className="text-[11px] text-muted-foreground">none</span>
                  : (
                    <div className="space-y-0.5">
                      {v.assignments.slice(-2).map((a) => (
                        <div key={`${a.work_item_id}-${a.assigned_at}`} className="text-[11px] text-foreground/80">{a.title}</div>
                      ))}
                    </div>
                  )}
              </Td>
              <Td>
                <div className="flex flex-wrap gap-1.5">
                  {can("volunteer", "clearance") && (
                    <Button size="sm" variant="outline" data-testid={`clearance-button-${v.id}`}
                            className="h-7 rounded-full px-2.5 text-[10px]"
                            onClick={() => {
                              setClearanceFor(v);
                              setClearance({
                                clearance_status: "cleared",
                                clearance_reference: v.clearance_reference || "",
                                clearance_expires_on: v.clearance_expires_on || "",
                              });
                            }}>
                      Clearance
                    </Button>
                  )}
                  {can("volunteer", "write") && (
                    <Button size="sm" variant="outline" data-testid={`assign-button-${v.id}`}
                            className="h-7 rounded-full px-2.5 text-[10px]"
                            onClick={() => { setAssignFor(v); setWorkItemId(""); setBlockNote(""); }}>
                      <UserPlus className="mr-1 h-3 w-3" /> Assign
                    </Button>
                  )}
                  {can("volunteer", "write") && (
                    <Button size="sm" variant="outline" data-testid={`log-time-${v.id}`}
                            className="h-7 rounded-full px-2.5 text-[10px]"
                            onClick={() => setTimeFor(v)}>
                      <Clock className="mr-1 h-3 w-3" /> Log time
                    </Button>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </Table>
      </Panel>

      <Panel testId="panel-sensitive-works" kicker="FR-VOL-003" title="Safeguarding-sensitive work items">
        <div className="divide-y divide-border">
          {sensitive.length === 0 && (
            <div className="px-4 py-6 text-xs text-muted-foreground">No work items are flagged as safeguarding-sensitive.</div>
          )}
          {sensitive.map((w) => (
            <div key={w.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`sensitive-work-${w.id}`}>
              <Chip className="border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300">clearance required</Chip>
              <span className="font-mono text-[11px] text-primary">{w.work_id}</span>
              <span className="flex-1 text-xs text-foreground/80">{w.title}</span>
              <span className="text-[11px] text-muted-foreground">{w.stage}</span>
              <span className="text-[11px] text-muted-foreground">
                {(w.assigned_volunteers || []).length} assigned
              </span>
            </div>
          ))}
        </div>
      </Panel>

      <Dialog open={Boolean(clearanceFor)} onOpenChange={(v) => !v && setClearanceFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Safeguarding clearance — {clearanceFor?.name}</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Recorded against the volunteer with the check date and expiry. Expired checks fail the gate.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <F label="Outcome">
              <Select value={clearance.clearance_status}
                      onValueChange={(v) => setClearance({ ...clearance, clearance_status: v })}>
                <SelectTrigger data-testid="clearance-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cleared">Cleared</SelectItem>
                  <SelectItem value="pending">Check submitted</SelectItem>
                  <SelectItem value="refused">Refused</SelectItem>
                  <SelectItem value="none">Withdrawn / not held</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Check reference">
              <Input value={clearance.clearance_reference}
                     onChange={(e) => setClearance({ ...clearance, clearance_reference: e.target.value })}
                     data-testid="clearance-reference-input" className="font-mono text-xs"
                     placeholder="DBS-2026-… / PVG-… / PBO-CHK-…" />
            </F>
            <F label="Expires on">
              <Input type="date" value={clearance.clearance_expires_on || ""}
                     onChange={(e) => setClearance({ ...clearance, clearance_expires_on: e.target.value })}
                     data-testid="clearance-expiry-input" className="font-mono" />
            </F>
          </div>
          <Button onClick={saveClearance} disabled={busy === "clearance"} data-testid="clearance-save-button"
                  className={PrimaryButtonClass}>
            {busy === "clearance" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Record clearance
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(assignFor)} onOpenChange={(v) => !v && setAssignFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign {assignFor?.name}</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Clearance state: <span className="font-mono">{assignFor?.clearance_state}</span>. Sensitive items
              are rejected unless the clearance is live.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <F label="Work item">
              <Select value={workItemId} onValueChange={setWorkItemId}>
                <SelectTrigger data-testid="assign-work-select"><SelectValue placeholder="Select a work item" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {works.map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.requires_safeguarding_clearance ? "⚠ " : ""}{w.work_id} · {w.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </F>
            {gateWouldBlock && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300"
                   data-testid="assign-gate-warning">
                This work item requires a live safeguarding clearance — {assignFor?.name}'s clearance is{" "}
                {assignFor?.clearance_state}. Record a clearance first.
              </div>
            )}
            {blockNote && (
              <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-600 dark:text-red-300"
                   data-testid="assign-block-banner">
                {blockNote}
              </div>
            )}
          </div>
          <Button onClick={assign} disabled={busy === "assign" || !workItemId || gateWouldBlock} data-testid="assign-confirm-button"
                  className={PrimaryButtonClass}>
            {busy === "assign" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Assign volunteer
          </Button>
        </DialogContent>
      </Dialog>

      <LogTimeDialog open={Boolean(timeFor)} onOpenChange={(o) => !o && setTimeFor(null)}
                     title={`Log time · ${timeFor?.name || ""}`} onLogged={load}
                     fixed={timeFor ? { person_kind: "volunteer", person_id: timeFor.id } : {}} />

      {detail.sheet}
    </div>
  );
}

const F = ({ label, children, full }) => (
  <div className={`space-y-1.5 ${full ? "col-span-2" : ""}`}>
    <Label className="text-[11px] text-muted-foreground">{label}</Label>
    {children}
  </div>
);
